import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Image as ImageIcon } from 'lucide-react';
import { api, getUploadsPublicUrl } from '../../services/api';
import { formatDateTime } from '../../utils/date';

export interface MaintenanceLogEntry {
  id: string;
  content: string;
  images: string[];
  createdAt: string;
  user?: { id: string; name: string; email: string };
}

interface MaintenanceTimelineProps {
  reportId: string;
}

/**
 * Línea de tiempo de la bitácora técnica (avances con evidencia), orden ascendente por fecha.
 */
export const MaintenanceTimeline: React.FC<MaintenanceTimelineProps> = ({ reportId }) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['maintenance-logs', reportId],
    queryFn: () => api.getMaintenanceTechnicianLogs(reportId),
    enabled: !!reportId,
  });

  if (isLoading) {
    return (
      <div className="py-6 text-center text-sm text-gray-500">
        <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-primary mb-2" />
        <p>Cargando bitácora…</p>
      </div>
    );
  }

  if (error) {
    const msg = error instanceof Error ? error.message : 'No se pudo cargar la bitácora';
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">{msg}</div>
    );
  }

  const logs = (data ?? []) as MaintenanceLogEntry[];
  if (logs.length === 0) {
    return (
      <p className="text-sm text-gray-500 py-2 flex items-center gap-2">
        <ImageIcon className="w-4 h-4 text-gray-400 shrink-0" />
        Aún no hay avances registrados en la bitácora.
      </p>
    );
  }

  return (
    <ul className="space-y-4 border-l-2 border-gray-200 ml-2 pl-4">
      {logs.map((entry) => (
        <li key={entry.id} className="relative">
          <span className="absolute -left-[21px] top-1.5 h-2.5 w-2.5 rounded-full bg-accent ring-4 ring-white" />
          <p className="text-xs text-gray-500">
            {entry.createdAt ? formatDateTime(entry.createdAt) : '—'}
            {entry.user?.name ? (
              <span className="text-gray-600"> · {entry.user.name}</span>
            ) : null}
          </p>
          <p className="mt-1 text-sm text-gray-800 whitespace-pre-wrap">{entry.content}</p>
          {entry.images && entry.images.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-2">
              {entry.images.map((url, i) => (
                <a
                  key={`${entry.id}-img-${i}`}
                  href={getUploadsPublicUrl(url)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block"
                >
                  <img
                    src={getUploadsPublicUrl(url)}
                    alt=""
                    className="h-20 w-20 object-cover rounded-lg border border-gray-200 hover:opacity-90"
                  />
                </a>
              ))}
            </div>
          )}
        </li>
      ))}
    </ul>
  );
};
